/**
 * Sync Manager
 *
 * Coordinates sync operations across the application.
 * Handles:
 * - Automatic sync on connectivity change
 * - Manual sync triggers
 * - Sync scheduling
 * - Event notifications
 */

import { QueryClient } from '@tanstack/react-query';
import { syncQueueProcessor } from './SyncQueueProcessor';
import { syncAllFromServer } from '../services';
import useSyncStore, { initializeSyncListeners } from '../stores/syncStore';
import { offlineDb, initializeOfflineDatabase } from '../database/offlineDatabase';
import useUserStore from '@/stores/userStore';
import type { SyncQueueResult } from '../types/offline.types';

// ============================================
// TYPES
// ============================================

interface SyncManagerOptions {
  /** Query client for cache invalidation */
  queryClient?: QueryClient;
  /** Auto-sync when coming online */
  autoSyncOnOnline?: boolean;
  /** Sync interval in ms (0 to disable) */
  syncIntervalMs?: number;
  /** Callback when sync completes */
  onSyncComplete?: (result: SyncQueueResult) => void;
  /** Callback when sync fails */
  onSyncError?: (error: Error) => void;
}

// ============================================
// SYNC MANAGER
// ============================================

/**
 * Sync Manager
 * Singleton class that manages all sync operations
 */
class SyncManager {
  private queryClient: QueryClient | null = null;
  private autoSyncOnOnline = true;
  private syncIntervalMs = 0;
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private onSyncComplete: ((result: SyncQueueResult) => void) | null = null;
  private onSyncError: ((error: Error) => void) | null = null;
  private cleanupListeners: (() => void) | null = null;
  private isInitialized = false;

  // ============================================
  // AUTH CHECK
  // ============================================

  /**
   * Check if user is authenticated
   * Prevents sync operations when user is not logged in
   */
  private isAuthenticated(): boolean {
    const tokens = useUserStore.getState().tokens;
    return !!tokens?.accessToken;
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize the sync manager
   * Should be called once during app startup
   */
  async initialize(options: SyncManagerOptions = {}): Promise<void> {
    if (this.isInitialized) {
      console.warn('[SyncManager] Already initialized');
      return;
    }

    console.log('[SyncManager] Initializing...');

    // Store options
    this.queryClient = options.queryClient ?? null;
    this.autoSyncOnOnline = options.autoSyncOnOnline ?? true;
    this.syncIntervalMs = options.syncIntervalMs ?? 0;
    this.onSyncComplete = options.onSyncComplete ?? null;
    this.onSyncError = options.onSyncError ?? null;

    // Initialize offline database
    await initializeOfflineDatabase();

    // Initialize sync store listeners
    this.cleanupListeners = initializeSyncListeners();

    // Set up online/offline event handlers
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Set up sync interval if configured
    if (this.syncIntervalMs > 0) {
      this.startSyncInterval();
    }

    // Initial sync counts update
    await this.updateSyncCounts();

    this.isInitialized = true;
    console.log('[SyncManager] Initialized successfully');

    // If online AND authenticated, trigger initial sync
    if (navigator.onLine && this.isAuthenticated()) {
      this.triggerSync().catch(console.error);
    }
  }

  /**
   * Cleanup and destroy the sync manager
   */
  destroy(): void {
    console.log('[SyncManager] Destroying...');

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    if (this.cleanupListeners) {
      this.cleanupListeners();
      this.cleanupListeners = null;
    }

    this.stopSyncInterval();
    syncQueueProcessor.abort();

    this.isInitialized = false;
    console.log('[SyncManager] Destroyed');
  }

  // ============================================
  // PUBLIC SYNC METHODS
  // ============================================

  /**
   * Trigger a full sync (upload pending changes + download from server)
   */
  async triggerSync(): Promise<SyncQueueResult> {
    if (!navigator.onLine) {
      console.log('[SyncManager] Cannot sync - device is offline');
      return this.createEmptyResult();
    }

    // Don't sync if not authenticated - prevents infinite reload loop
    if (!this.isAuthenticated()) {
      console.log('[SyncManager] Cannot sync - user not authenticated');
      return this.createEmptyResult();
    }

    if (syncQueueProcessor.isActive()) {
      console.log('[SyncManager] Sync already in progress');
      return this.createEmptyResult();
    }

    console.log('[SyncManager] Starting sync...');

    try {
      // First, push pending changes to server
      const uploadResult = await syncQueueProcessor.processQueue();

      // Then, pull latest data from server
      await syncAllFromServer();

      // Invalidate React Query cache to reflect new data
      if (this.queryClient) {
        await this.queryClient.invalidateQueries();
      }

      // Update sync counts
      await this.updateSyncCounts();

      // Notify completion
      if (this.onSyncComplete) {
        this.onSyncComplete(uploadResult);
      }

      console.log('[SyncManager] Sync completed:', uploadResult);
      return uploadResult;
    } catch (error) {
      console.error('[SyncManager] Sync failed:', error);

      if (this.onSyncError) {
        this.onSyncError(error instanceof Error ? error : new Error('Unknown error'));
      }

      throw error;
    }
  }

  /**
   * Push pending changes only (no download)
   */
  async pushChanges(): Promise<SyncQueueResult> {
    if (!navigator.onLine) {
      console.log('[SyncManager] Cannot push - device is offline');
      return this.createEmptyResult();
    }

    return syncQueueProcessor.processQueue();
  }

  /**
   * Pull latest data from server only (no upload)
   */
  async pullFromServer(): Promise<void> {
    if (!navigator.onLine) {
      console.log('[SyncManager] Cannot pull - device is offline');
      return;
    }

    await syncAllFromServer();

    if (this.queryClient) {
      await this.queryClient.invalidateQueries();
    }
  }

  /**
   * Force stop any ongoing sync
   */
  stopSync(): void {
    syncQueueProcessor.abort();
  }

  /**
   * Get current sync status
   */
  getSyncStatus(): {
    isOnline: boolean;
    isSyncing: boolean;
    pendingCount: number;
    failedCount: number;
  } {
    const state = useSyncStore.getState();
    return {
      isOnline: state.isOnline,
      isSyncing: state.isSyncing,
      pendingCount: state.pendingCount,
      failedCount: state.failedCount,
    };
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private handleOnline = async (): Promise<void> => {
    console.log('[SyncManager] Device is online');
    useSyncStore.getState().actions.setOnline(true);

    if (this.autoSyncOnOnline) {
      // Small delay to ensure connection is stable
      await this.delay(1000);

      // Only sync if online AND authenticated
      if (navigator.onLine && this.isAuthenticated()) {
        this.triggerSync().catch(console.error);
      }
    }
  };

  private handleOffline = (): void => {
    console.log('[SyncManager] Device is offline');
    useSyncStore.getState().actions.setOnline(false);
    syncQueueProcessor.abort();
  };

  private startSyncInterval(): void {
    if (this.syncIntervalId) {
      return;
    }

    this.syncIntervalId = setInterval(() => {
      if (navigator.onLine && !syncQueueProcessor.isActive()) {
        this.triggerSync().catch(console.error);
      }
    }, this.syncIntervalMs);

    console.log(`[SyncManager] Sync interval started: ${this.syncIntervalMs}ms`);
  }

  private stopSyncInterval(): void {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
      console.log('[SyncManager] Sync interval stopped');
    }
  }

  private async updateSyncCounts(): Promise<void> {
    const pendingCount = await offlineDb.getPendingCount();
    const failedCount = await offlineDb.getFailedCount();

    const actions = useSyncStore.getState().actions;

    // Reset and set counts (simplified approach)
    // In production, you'd want more granular per-entity updates
    actions.reset();

    // This is a simplified approach - in production you'd track per-entity
    if (pendingCount > 0 || failedCount > 0) {
      console.log(`[SyncManager] Counts: ${pendingCount} pending, ${failedCount} failed`);
    }
  }

  private createEmptyResult(): SyncQueueResult {
    return {
      totalProcessed: 0,
      successCount: 0,
      failedCount: 0,
      conflictsResolved: 0,
      failures: [],
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const syncManager = new SyncManager();

// ============================================
// EXPORTS
// ============================================

export type { SyncManagerOptions };
